/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Grid, Stack } from "@mui/material";
import axios from "../../../api/axios";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PageShell, { SectionCard } from "components/patch/PageShell";
import PayrollRunPicker from "components/patch/PayrollRunPicker";
import DataTable from "examples/Tables/DataTable";

function getRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function getMeta(payload) {
  if (payload?.current_page) return payload;
  if (payload?.data?.current_page) return payload.data;
  return { current_page: 1, last_page: 1, total: 0 };
}

function downloadFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const badgeColor = (severity) => {
  if (severity === "critical") return "error";
  if (severity === "warning") return "warning";
  return "info";
};

function SeverityCell({ value }) {
  return <Chip size="small" color={badgeColor(value)} label={value || "info"} />;
}

SeverityCell.propTypes = {
  value: PropTypes.string,
};

SeverityCell.defaultProps = {
  value: "",
};

function StatusCell({ value }) {
  return <Chip size="small" variant="outlined" label={value || "open"} />;
}

StatusCell.propTypes = {
  value: PropTypes.string,
};

StatusCell.defaultProps = {
  value: "",
};

function ActionCell({ row, onResolve, onReopen }) {
  const item = row?.original || {};

  return item.status === "resolved" ? (
    <MDButton size="small" color="warning" variant="text" onClick={() => onReopen(item.id)}>
      Reopen
    </MDButton>
  ) : (
    <MDButton size="small" color="success" variant="text" onClick={() => onResolve(item.id)}>
      Resolve
    </MDButton>
  );
}

ActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      status: PropTypes.string,
    }),
  }),
  onResolve: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
};

ActionCell.defaultProps = {
  row: { original: {} },
};

export default function PayrollExceptions() {
  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    severity: "",
    status: "open",
    search: "",
    page: 1,
    per_page: 50,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const runId = run?.id;

  const load = useCallback(
    async (next = {}) => {
      if (!runId) return;

      const params = { ...filters, ...next };

      setFilters(params);
      setLoading(true);
      setMessage("");

      try {
        const [listRes, summaryRes] = await Promise.all([
          axios.get(`/payroll-run/${runId}/exceptions`, { params }),
          axios.get(`/payroll-run/${runId}/exceptions/summary`),
        ]);

        setRows(getRows(listRes.data));
        setMeta(getMeta(listRes.data));
        setSummary(summaryRes.data?.summary || summaryRes.data || null);
      } catch (error) {
        setMessage(error?.response?.data?.message || "Unable to load payroll exceptions.");
      } finally {
        setLoading(false);
      }
    },
    [runId, filters]
  );

  useEffect(() => {
    if (runId) load({ page: 1 });
  }, [runId]);

  const rebuild = async () => {
    if (!runId) return;

    setLoading(true);
    setMessage("");

    try {
      const res = await axios.post(`/payroll-run/${runId}/exceptions/rebuild`);
      setSummary(res.data?.summary || null);
      setMessage(res.data?.message || "Payroll exception validation completed.");
      await load({ page: 1 });
    } catch (error) {
      setMessage(error?.response?.data?.message || "Validation failed.");
    } finally {
      setLoading(false);
    }
  };

  const resolveException = useCallback(
    async (id) => {
      const resolution_note = window.prompt("Enter resolution note / approval reference");
      if (!resolution_note) return;

      setLoading(true);

      try {
        await axios.post(`/payroll-run/exceptions/${id}/resolve`, { resolution_note });
        await load();
      } catch (error) {
        setMessage(error?.response?.data?.message || "Unable to resolve exception.");
      } finally {
        setLoading(false);
      }
    },
    [load]
  );

  const reopenException = useCallback(
    async (id) => {
      setLoading(true);

      try {
        await axios.post(`/payroll-run/exceptions/${id}/reopen`);
        await load();
      } catch (error) {
        setMessage(error?.response?.data?.message || "Unable to reopen exception.");
      } finally {
        setLoading(false);
      }
    },
    [load]
  );

  const exportCsv = async () => {
    if (!runId) return;

    const res = await axios.get(`/payroll-run/${runId}/exceptions/export.csv`, {
      responseType: "blob",
      params: { status: filters.status || undefined },
    });

    downloadFile(res.data, `payroll_exceptions_run_${runId}_${run?.month || ""}.csv`);
  };

  const columns = useMemo(
    () => [
      { Header: "Severity", accessor: "severity", Cell: SeverityCell },
      { Header: "Status", accessor: "status", Cell: StatusCell },
      { Header: "Type", accessor: "exception_type" },
      { Header: "PF No", accessor: "pf_no" },
      { Header: "Staff", accessor: "staff_name" },
      { Header: "Department", accessor: "department" },
      { Header: "Message", accessor: "message" },
      {
        Header: "Action",
        accessor: "id",
        Cell: ({ row }) => (
          <ActionCell row={row} onResolve={resolveException} onReopen={reopenException} />
        ),
      },
    ],
    [resolveException, reopenException]
  );
  return (
    <PageShell
      title="Payroll Exceptions"
      subtitle="Validate payroll runs before finalization. Critical exceptions block approval/finalization until resolved."
      actions={
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <MDButton color="info" onClick={rebuild} disabled={!runId || loading}>
            Validate / Rebuild
          </MDButton>
          <MDButton color="secondary" variant="outlined" onClick={exportCsv} disabled={!runId}>
            Export CSV
          </MDButton>
        </Stack>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SectionCard title="Select payroll run">
            <PayrollRunPicker onSelected={setRun} withMonth />
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Exception list" subtitle={`${meta.total || 0} record(s)`}>
            <DataTable
              table={{ columns, rows }}
              entriesPerPage={false}
              showTotalEntries={false}
              isSorted={false}
              noEndBorder
            />
          </SectionCard>
        </Grid>
      </Grid>
    </PageShell>
  );
}
